import React, { useContext, useState, useEffect, Fragment, useCallback, useMemo } from 'react';
import Layout from '../../components/layout/main';
import { makeStyles } from 'tss-react/mui';
import { Fade, Grid, Paper } from '@mui/material';
import * as yup from 'yup';
import { postData, getData, putData, getDataFull } from '../../services/ajax';
import { AuthContext } from '../../context/auth';
import { LoadingModalContext } from '../../components/modal/loading';
import { AlertModalContext } from '../../components/modal/alert';
import { setYupLocaleES } from '../../const/yup';
import { useNavigate } from 'react-router-dom';
import { CustomForm } from '../../components/formSections';
import ProcessGuide from '../../components/orden/ProcessGuide';
import { GlobalsContext } from '../../context/globals';
import { useSnackbar } from 'notistack';

setYupLocaleES();

const useStyles = makeStyles()((theme) => ({
    paper: {
        padding: theme.spacing(4),
        paddingBottom: theme.spacing(2),
        display: 'flex',
        overflow: 'auto',
        flexDirection: 'column',
        width: '100%',
        marginBottom: theme.spacing(2),
    },
}));

function Page() {
    const { classes } = useStyles();
    const { usuario } = useContext(AuthContext);
    const navigate = useNavigate();
    const globalsContext = useContext(GlobalsContext);
    const [data, setData] = useState(null);
    const [establecimientos, setEstablecimientos] = useState([]);

    const loadingModal = useContext(LoadingModalContext);
    const alertModal = useContext(AlertModalContext);
    const { enqueueSnackbar } = useSnackbar();

    useEffect(() => {
        getDataFull('establecimientos?per_page=500', usuario.token).then((r) => {
            if (r.error === '' && r.data) setEstablecimientos(r.data);
        });
    }, [usuario.token]);

    const onSubmit = async (values) => {
        loadingModal.setOpen(true);
        try {
            const nivel = Number(values.nivel_usuario);
            if (nivel === 3 && !values.establecimiento_id) {
                enqueueSnackbar('Los operarios deben tener un establecimiento asignado.', { variant: 'error' });
                loadingModal.setOpen(false);
                return;
            }

            const usuarioData = {
                email: values.email,
                cuil: values.cuil ? Number(values.cuil) : null,
                nombre_apellido: values.nombre_apellido,
                nivel_usuario: nivel,
                sup_usuarios: Boolean(values.sup_usuarios),
                establecimiento_id: nivel === 1 ? null : Number(values.establecimiento_id),
            };

            let response;
            if (data && data.id_usuario) {
                response = await putData(`usuarios/${data.id_usuario}`, usuarioData, usuario.token);
            } else {
                response = await postData('usuarios', usuarioData, usuario.token);
            }

            if (response.error === '') {
                enqueueSnackbar(
                    data
                        ? 'Usuario modificado correctamente'
                        : 'Usuario creado correctamente. Se ha enviado un email con el enlace para crear la contraseña.',
                    { variant: 'success' }
                );
                onClose();
            } else {
                enqueueSnackbar(response.error, { variant: 'error' });
            }
        } catch (error) {
            enqueueSnackbar('Error al guardar el usuario', { variant: 'error' });
        } finally {
            loadingModal.setOpen(false);
        }
    };

    const onClose = useCallback(() => navigate(-1), [navigate]);

    const onCancel = () => {
        alertModal.showModal('Cancelar', 'Se perderán todos los cambios. ¿Deseas continuar?', () => onClose());
    };

    const loadData = useCallback(async () => {
        if (!globalsContext.idEdit) return;
        loadingModal.setOpen(true);
        try {
            const response = await getData(`usuarios/${globalsContext.idEdit}`, usuario.token);
            if (response.error === '') {
                setData({
                    ...response.data,
                    nombre: response.data.nombre_apellido,
                    cuitCuil: response.data.cuil,
                });
            } else {
                enqueueSnackbar(response.error, { variant: 'error' });
            }
        } catch (error) {
            enqueueSnackbar('Error al cargar los datos del usuario', { variant: 'error' });
        } finally {
            loadingModal.setOpen(false);
        }
    }, [globalsContext.idEdit, usuario.token]);

    useEffect(() => {
        if (globalsContext.idEdit) loadData();
    }, [globalsContext.idEdit, loadData]);

    const establecimientoItems = useMemo(
        () => establecimientos.map((e) => ({
            id: e.id,
            nombre: `${e.nombre}${e.tipo ? ` (${e.tipo})` : ''}`,
        })),
        [establecimientos]
    );

    const formSections = useMemo(() => [
        {
            name: 'Datos personales',
            fields: [
                {
                    dataName: 'email',
                    label: 'Email',
                    controlType: 'textField',
                    initialValue: data ? data.email : '',
                    validationSchema: yup.string().email('Debe ser un email válido').required('El email es requerido'),
                    autoFocus: true,
                },
                {
                    dataName: 'cuil',
                    label: 'CUIT / CUIL',
                    controlType: 'textField',
                    initialValue: data ? data.cuitCuil : '',
                    validationSchema: yup.number().nullable().min(20000000000, 'CUIT/CUIL inválido').max(31000000000, 'CUIT/CUIL inválido'),
                },
                {
                    dataName: 'nombre_apellido',
                    label: 'Nombre y apellido',
                    controlType: 'textField',
                    initialValue: data ? data.nombre : '',
                    validationSchema: yup.string().required('El nombre es requerido'),
                },
            ],
        },
        {
            name: 'Permisos y nivel',
            description: 'Administrador gestiona el sistema. Operario opera desde un establecimiento (público o privado).',
            fields: [
                {
                    dataName: 'nivel_usuario',
                    label: 'Nivel de usuario',
                    controlType: 'select',
                    items: [
                        { id: 1, nombre: 'Administrador' },
                        { id: 3, nombre: 'Operario' },
                    ],
                    initialValue: data ? data.nivel_usuario : 3,
                    validationSchema: yup.number().required().oneOf([1, 3]),
                },
                {
                    dataName: 'establecimiento_id',
                    label: 'Establecimiento',
                    controlType: 'select',
                    items: [{ id: '', nombre: '(Solo admin sin establecimiento)' }, ...establecimientoItems],
                    initialValue: data?.establecimiento_id != null ? data.establecimiento_id : '',
                    validationSchema: yup.mixed().nullable(),
                },
                {
                    dataName: 'sup_usuarios',
                    label: 'Supervisión de Usuarios',
                    controlType: 'checkBox',
                    initialValue: data ? data.sup_usuarios : false,
                    validationSchema: yup.boolean(),
                },
            ],
        },
    ], [data, establecimientoItems]);

    const formInitialValues = useMemo(() => ({
        email: data ? data.email : '',
        cuil: data ? data.cuitCuil : '',
        nombre_apellido: data ? data.nombre : '',
        nivel_usuario: data ? data.nivel_usuario : 3,
        establecimiento_id: data?.establecimiento_id != null ? data.establecimiento_id : '',
        sup_usuarios: data ? Boolean(data.sup_usuarios) : false,
    }), [data]);

    return (
        <Fragment>
            <Layout nombrePagina={data ? 'Modificación de Usuario' : 'Nuevo Usuario'}>
                <Fade in timeout={900}>
                    <Grid size={12}>
                        <ProcessGuide
                            title="Cómo completar el usuario"
                            steps={[
                                'Administrador: sin establecimiento. Operario: elija el establecimiento al que pertenece.',
                                'El tipo del establecimiento define si podrá cargar órdenes o solo responderlas.',
                            ]}
                        />
                        <Paper className={classes.paper}>
                            <CustomForm
                                key={data ? `u-${data.id_usuario}` : 'u-new'}
                                formSections={formSections}
                                initialValues={formInitialValues}
                                onSubmit={onSubmit}
                                onCancel={onCancel}
                                submitButtonText={data ? 'Guardar cambios' : 'Crear usuario'}
                                cancelButtonText="Cancelar"
                                stepperType="vertical"
                                isModal={false}
                            />
                        </Paper>
                    </Grid>
                </Fade>
            </Layout>
        </Fragment>
    );
}

export default Page;
